import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";
import InfoTooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import RightworkSelect from "../../../../../components/shared/RightworkSelect";
import { downloadCSV, objectToCSV } from "../../../../../utilities/csvExport";
import PrebuiltReportShell from "../PrebuiltReportShell";
import { getEligibleStoresForReport } from "../reportAccess";
import { lastCompletedBusinessDay } from "./_shared/reportDates";
import { rangeError } from "./_shared/laborFlags";
import { formatCurrency } from "./_shared/laborMath";
import { accuracyDetailExportRows, accuracyInputIssues, accuracySeries, loadAccuracyReport } from "./_shared/forecastAccuracy";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend } from "recharts";

const PAGE_SIZE = 100;
const outcomeLabels = { improved: "Improved", worsened: "Worsened", unchanged: "No accuracy change", unadjusted: "Unadjusted" };
function Info({ label, children }) {
  const [open, setOpen] = useState(false);
  return <ClickAwayListener onClickAway={() => setOpen(false)}><span className="inline-flex align-middle">
    <InfoTooltip title={children} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} describeChild disableTouchListener>
      <IconButton size="small" aria-label={`About ${label}`} onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}><InfoOutlined sx={{ fontSize: 17 }} /></IconButton>
    </InfoTooltip>
  </span></ClickAwayListener>;
}
function Switch({ label, value, options, onChange }) {
  return <div role="group" aria-label={label} className="flex flex-wrap gap-2">{options.map(([key, name]) => <Button key={key} size="small" variant={value === key ? "contained" : "outlined"} aria-pressed={value === key} sx={actionSx} onClick={() => onChange(key)}>{name}</Button>)}</div>;
}
const pct = (v) => v == null ? "—" : `${v.toFixed(2)}%`;
const actionSx = { textTransform: "none" };
// Match the shared select's dimensions, labels, neutral border and focus states.
const datePickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 42, backgroundColor: "white", borderRadius: "8px", fontSize: "14px", color: "#334155",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "action.disabled" },
  },
};

function Filter({ id, label, children }) {
  return <div className="w-[240px] sm:w-full">
    <label id={`${id}-label`} htmlFor={id} className="block mb-1 text-xs font-medium text-slate-600">{label}</label>
    {children}
  </div>;
}

function AccuracyTable({ headers, rows }) {
  return <div tabIndex={0} role="region" aria-label="Scrollable report table" className="overflow-x-auto rounded-lg border border-slate-200 focus-visible:outline-violet-700">
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead className="bg-slate-50 text-slate-600"><tr>{headers.map((h) => <th scope="col" key={typeof h === "string" ? h : headers.indexOf(h)} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-100">{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} className={`px-4 py-3 align-top text-slate-700 ${j < row.length - 1 ? "whitespace-nowrap" : ""}`}>{cell}</td>)}</tr>)}</tbody>
    </table>
  </div>;
}

export default function ForecastAccuracyReport({ report, onBack, homeStore, homeCompany, companyContext = homeCompany, storeNames, storeOptions }) {
  // Require a loaded membership list: never issue requests during the
  // registry's intentional initial-loading fallback.
  const eligible = storeOptions?.length ? getEligibleStoresForReport(report, storeNames, storeOptions) : [];
  const companyId = Number(companyContext);
  const stores = eligible.filter((s) => Number(s.company) === companyId).sort((a, b) => a.store_name.localeCompare(b.store_name));
  const [locationPick, setLocationPick] = useState(null);
  const locationIds = locationPick?.company === companyId ? locationPick.ids : null;
  const selectedStores = locationIds === null ? stores : stores.filter((s) => locationIds.includes(s.id));
  const [start, setStart] = useState(() => dayjs(lastCompletedBusinessDay(storeNames, homeStore)).subtract(6, "day"));
  const [end, setEnd] = useState(() => dayjs(lastCompletedBusinessDay(storeNames, homeStore)));
  const [preset, setPreset] = useState("custom");
  const [view, setView] = useState("summary");
  const [chartView, setChartView] = useState("chart");
  const [grain, setGrain] = useState("day");
  const [page, setPage] = useState(0);
  const [request, setRequest] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const workQueue = useRef(Promise.resolve());
  const validation = rangeError(start, end) || (!selectedStores.length ? "Select at least one eligible location." : null);
  const filterKey = JSON.stringify({ companyId, stores: selectedStores.map((s) => [s.id, s.store_name]), start: start?.format("YYYY-MM-DD"), end: end?.format("YYYY-MM-DD") });
  const current = !validation && result?.key === filterKey ? result.data : null;
  const pending = loading && request?.key === filterKey;

  useEffect(() => {
    let cancelled = false;
    if (!request) return undefined;
    if (request.key !== filterKey) {
      setRequest(null);
      setResult(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setProgress("Loading forecasts and sales…");
    setResult(null);
    setError(null);
    setPage(0);
    // A cancelled request may still be executing. Serialize explicit reruns
    // behind it, and discard late results or superseded queued work.
    const task = workQueue.current.then(() => loadAccuracyReport(request.stores, request.start, request.end, {
      isCancelled: () => cancelled,
      onProgress: (message) => { if (!cancelled) setProgress(message); },
    }));
    workQueue.current = task.catch(() => {});
    task.then((data) => { if (!cancelled) setResult({ key: request.key, data }); })
      .catch(() => { if (!cancelled) setError({ key: request.key, message: "Couldn't load all selected locations. No partial results are shown." }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, filterKey]);

  function run() {
    if (validation || pending) return;
    setRequest({ key: filterKey, stores: selectedStores, start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") });
  }

  function choosePreset(value) {
    setPreset(value);
    if (value === "custom") return;
    const today = dayjs(lastCompletedBusinessDay(storeNames, selectedStores[0]?.id || homeStore));
    if (value === "last30") { setStart(today.subtract(29, "day")); setEnd(today); return; }
    if (value === "lastYear") { setStart(today.subtract(1, "year").startOf("year")); setEnd(today.subtract(1, "year").endOf("year")); return; }
    if (value === "year") { setStart(today.startOf("year")); setEnd(today); return; }
    const quarterStart = today.month(Math.floor(today.month() / 3) * 3).startOf("month");
    setStart(value === "lastQuarter" ? quarterStart.subtract(3, "month") : quarterStart);
    setEnd(value === "lastQuarter" ? quarterStart.subtract(1, "day") : today);
  }

  const filters = <div className="flex flex-wrap items-end gap-4">
    <Filter id="accuracy-locations" label="Select locations"><RightworkSelect id="accuracy-locations" multiple value={selectedStores.map((s) => s.id)} options={stores.map((s) => ({ value: s.id, label: s.store_name }))} renderValue={(ids) => `${ids.length} locations selected`} onChange={(e) => setLocationPick({ company: companyId, ids: e.target.value })} /></Filter>
    <Filter id="accuracy-period" label="Period"><RightworkSelect id="accuracy-period" value={preset} onChange={(e) => choosePreset(e.target.value)} options={[{ value: "custom", label: "Custom dates" }, { value: "last30", label: "Last 30 completed days" }, { value: "quarter", label: "Quarter to date" }, { value: "lastQuarter", label: "Last quarter" }, { value: "year", label: "Year to date" }, { value: "lastYear", label: "Last year" }]} /></Filter>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Filter id="accuracy-start-date" label="Start date">
        <DesktopDatePicker value={start} onChange={(d) => { setPreset("custom"); setStart(d); }} sx={datePickerSx} slotProps={{ textField: { id: "accuracy-start-date", size: "small" } }} />
      </Filter>
      <Filter id="accuracy-end-date" label="End date">
        <DesktopDatePicker value={end} onChange={(d) => { setPreset("custom"); setEnd(d); }} sx={datePickerSx} slotProps={{ textField: { id: "accuracy-end-date", size: "small" } }} />
      </Filter>
    </LocalizationProvider>
    <Button variant="outlined" sx={{ ...actionSx, height: 42 }} disabled={!!validation || pending} onClick={run}>Run report</Button>
    {pending && <Button sx={actionSx} onClick={() => { setRequest(null); setLoading(false); }}>Cancel</Button>}
  </div>;
  const rows = current?.rows.filter((r) => !r.exclusion) || [];
  const locations = current?.summary.filter((r) => r.scored > 0) || [];
  const series = accuracySeries(rows, grain);
  const issues = current ? accuracyInputIssues(current.rows) : [];
  const exportRows = !rows.length ? [] : view === "summary"
    ? [...locations, { location: "Overall", ...current.total }].map((r) => ({ location: r.location,
      baseline_mape: r.baseline_mape, adjusted_mape: r.adjusted_mape, baseline_wape: r.baseline_wape,
      adjusted_wape: r.adjusted_wape, improved: r.improved, worsened: r.worsened,
      no_accuracy_change: r.unchanged, unadjusted: r.unadjusted }))
    : view === "trend" ? series.map((r) => ({ date: r.date, baseline_mape: r.baseline_mape, adjusted_mape: r.adjusted_mape }))
      : accuracyDetailExportRows(rows);
  const actions = <Button variant="outlined" size="small" sx={actionSx} disabled={!rows.length || pending} onClick={() => downloadCSV(objectToCSV(exportRows), `Forecast_Accuracy_${view}_${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}.csv`)}>Download CSV</Button>;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visiblePage = Math.min(page, pageCount - 1);

  return <PrebuiltReportShell report={{ ...report, description: null }} onBack={onBack} filters={filters} actions={actions}>
    {validation && <p role="alert" className="mb-4 text-sm text-amber-800">{validation}</p>}
    {pending && <p role="status" className="rounded-lg bg-violet-50 p-4 text-sm text-violet-800">{progress || "Loading results…"} Large date ranges can take several minutes.</p>}
    {!pending && error?.key === filterKey && <p role="alert" className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800">{error.message} <Button sx={actionSx} onClick={run}>Retry</Button></p>}
    {!pending && !current && error?.key !== filterKey && <p className="rounded-lg border border-slate-200 p-6 text-sm text-slate-500">Choose locations and dates, then run the report.</p>}
    {!pending && current && <>
      {issues.length > 0 && <p role="status" className="mb-3 text-sm text-amber-800">{issues.join(" ")}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Switch label="Report view" value={view} onChange={(v) => { setView(v); setPage(0); }} options={[["summary", "Locations"], ["detail", "Daily results"], ["trend", "Over time"]]} />
        <Info label="forecast comparison">Latest saved baseline and adjusted forecasts compared with actual sales for completed days.</Info>
      </div>
      {!rows.length ? <p className="py-6 text-sm text-slate-500">No forecast results for these dates.</p> : view === "summary" ?
        <AccuracyTable headers={["Location", <span>Baseline MAPE<Info label="MAPE">Average absolute percentage error. Lower is better. Both forecasts use the same days with sales data.</Info></span>, "Adjusted MAPE", <span>Baseline WAPE<Info label="WAPE">Absolute error divided by sales. Higher-sales days carry more weight.</Info></span>, "Adjusted WAPE", "Improved", "Worsened", <span>No accuracy change<Info label="no accuracy change">An adjustment was made, but it was equally far from actual sales as the baseline. Unadjusted means no active adjustment.</Info></span>, "Unadjusted"]} rows={[...locations, { location: "Overall", ...current.total }].map((r) => [r.location, pct(r.baseline_mape), pct(r.adjusted_mape), pct(r.baseline_wape), pct(r.adjusted_wape), r.improved, r.worsened, r.unchanged, r.unadjusted])} />
        : view === "detail" ? <>
          <AccuracyTable headers={["Location", "Date", "Baseline", "Adjusted", "Actual sales", "Baseline error (%)", "Adjusted error (%)", "Result"]} rows={rows.slice(visiblePage * PAGE_SIZE, (visiblePage + 1) * PAGE_SIZE).map((r) => [r.location, r.date, formatCurrency(r.baseline), formatCurrency(r.adjusted), formatCurrency(r.actual), pct(r.baseline_ape), pct(r.adjusted_ape), outcomeLabels[r.outcome]])} />
          {pageCount > 1 && <nav aria-label="Result pages" className="flex gap-2 items-center mt-3"><Button disabled={visiblePage === 0} onClick={() => setPage(visiblePage - 1)}>Previous</Button><span>Page {visiblePage + 1} of {pageCount}</span><Button disabled={visiblePage + 1 >= pageCount} onClick={() => setPage(visiblePage + 1)}>Next</Button></nav>}
        </> : <section aria-label="Forecast error over time">
          <div className="flex flex-wrap justify-between gap-3 mb-4">
            <Switch label="Time interval" value={grain} onChange={setGrain} options={[["day", "Daily"], ["week", "Weekly"]]} />
            <Switch label="Time series display" value={chartView} onChange={setChartView} options={[["chart", "Chart"], ["table", "Table"]]} />
          </div>
          {chartView === "chart" ? <div style={{ width: "100%", height: 280 }} role="img" aria-label="Baseline and adjusted MAPE over time. Select Table for exact values."><ResponsiveContainer><LineChart data={series}><XAxis dataKey="date" /><YAxis unit="%" /><Tooltip formatter={pct} /><Legend /><Line dataKey="baseline_mape" name="Baseline MAPE" stroke="#6d28d9" connectNulls={false} isAnimationActive={false} strokeWidth={2} /><Line dataKey="adjusted_mape" name="Adjusted MAPE" stroke="#0f766e" connectNulls={false} isAnimationActive={false} strokeWidth={2} /></LineChart></ResponsiveContainer></div> : <AccuracyTable headers={[grain === "week" ? "Week starting Monday" : "Date", "Baseline MAPE", "Adjusted MAPE"]} rows={series.map((r) => [r.date, pct(r.baseline_mape), pct(r.adjusted_mape)])} />}
        </section>}

    </>}
  </PrebuiltReportShell>;
}
